import { IconsId } from '@types';
import clsx from 'clsx';
import React, { InputHTMLAttributes, useState } from 'react';

import { FormError } from '../FormElements/FormElements';
import { Icon } from '../Icon/Icon';

import styles from './Input.module.scss';

function toIntegerString(value: string): string {
  const s = value.replace(/[^\d-]/g, '');
  const hasMinus = s.startsWith('-');
  const digits = s.replace(/-/g, '');
  return hasMinus ? `-${digits}` : digits;
}

type InputProps = {
  label?: string;
  className?: string;
  inputClassName?: string;
  props?: InputHTMLAttributes<HTMLInputElement> & { ref?: React.ForwardedRef<HTMLInputElement> } & Partial<
      Record<`data-${string}`, string>
    >;
  error?: string | React.ReactNode;
  isDisabled?: boolean;
  icon?: string | IconsId;
  hideEye?: boolean;
  animated?: boolean;
  onClick?: () => void;
};

export const Input: React.FC<InputProps> = ({
  animated,
  label,
  className,
  inputClassName,
  error,
  props,
  isDisabled,
  hideEye,
  icon,
  onClick,
}) => {
  const isPassword = props?.type === 'password';
  const [type, setType] = useState(props?.type || 'text');
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (props?.inputMode === 'numeric') {
      const next = toIntegerString(e.target.value);
      if (next !== e.target.value) e.target.value = next;
    }
    props?.onChange?.(e);
  };

  return (
    <div
      className={clsx(className, styles.inputBox, {
        [styles.hasError]: !!error,
        [styles.disabled]: isDisabled,
        [styles.hasIcon]: icon,
        [styles.animated]: animated,
      })}
      onClick={onClick}
      ref={props?.ref}
    >
      <input
        {...props}
        disabled={isDisabled}
        ref={props?.ref}
        id={props?.id}
        value={props?.value}
        placeholder={props?.placeholder || ' '}
        className={clsx(styles.input, inputClassName)}
        type={type}
        onChange={handleChange}
      />
      {animated && label && (
        <label
          htmlFor={props?.id}
          className={styles.label}
        >
          {label}
        </label>
      )}
      {isPassword && !hideEye && (
        <Icon
          name={type === 'password' ? 'eye-close' : 'eye'}
          onClick={() => setType(type === 'password' ? 'text' : 'password')}
          className={styles.eyeIcon}
        />
      )}

      {error && <FormError error={error} />}
    </div>
  );
};
